// @flow

import React from 'react';
import { I18n } from 'react-redux-i18n';
import TuileRow from './TuileRow';
import ImageHeader from '../ImageHeader';
import { convertToPercentile, formatRoi, toFixedOrElse } from '../../../../utils/formatUtils';
import type { CoqueRetailer, Extremas, Store } from '../../../../types/apolloType';
import CurrencySurfaceConvertor from '../../../common/CurrencySurfaceConvertor';
import SurfaceConvertor from '../../../common/SurfaceConvertor';
import IconButton from '../../../../components/IconButton';

type Props = {
  currentStore: CoqueRetailer,
  mainRetailer: Store,
  viewOffer: Function,
  extrema: Extremas,
  deleteStore: Function,
  searchCriterias: any,
  generateM30: Function,
  isGenerationDisabled: boolean,
  isGenerationLoading: boolean,
  isBp: boolean,
  isLocomotive: boolean,
}

const ROI_MIN = 0.12;

function TuileComparator({
  currentStore, mainRetailer, viewOffer, extrema, deleteStore, searchCriterias,
  generateM30, isGenerationDisabled, isGenerationLoading, isBp, isLocomotive,
}: Props) {
  const { roiResult: { roi, netMgrGlaUplift, tic } } = currentStore;
  return (
    // Rectangle de base
    <div className="comparator ">
      <ImageHeader
        retailer={currentStore}
        deleteStore={deleteStore}
        searchCriterias={searchCriterias}
        isBp={isBp}
        isLocomotive={isLocomotive}
      />
      <div className="tile-row-container">
        <TuileRow
          isGreen={false}
          unit=""
          label={currentStore.branchName}
          isMainRetailer
        />
        <div className="tile-row">
          <div className="retailer-values">
            <div className="force-align-center">
              <SurfaceConvertor
                value={toFixedOrElse(currentStore.gla, 2, 0)}
                isBold
              />/&nbsp;
              {(currentStore.gla - currentStore.glaExclSst) !== 0 ?
                (
                  <SurfaceConvertor
                    value={
                      (
                        currentStore.gla - currentStore.glaExclSst
                      ).toFixed(2)
                    }
                  />
                ) : ''
              }
            </div>
          </div>
        </div>
        <TuileRow
          label=""
          isMainRetailer={false}
        />
        <TuileRow
          isBig={
            currentStore.projectedErv === extrema.ervMax
            && currentStore.projectedErv.average >= mainRetailer.erv
          }
          isGreen={currentStore.projectedErv.average >= mainRetailer.rrv}
          label={
            <CurrencySurfaceConvertor
              value={toFixedOrElse(currentStore.projectedErv.average, 2, null)}
              withSymbol
              mustBeCurrencyBySurface
            />
          }
          isMainRetailer={false}
        />
        <TuileRow
          isBig={currentStore.projectedMgr.average === extrema.mgrMax
          && currentStore.projectedMgr.average >= mainRetailer.mgr}
          isGreen={currentStore.projectedMgr.average >= mainRetailer.mgr}
          label={
            currentStore.projectedMgr.average ?
              <CurrencySurfaceConvertor
                value={currentStore.projectedMgr.average.toFixed(2)}
                withSymbol
                mustBeCurrencyBySurface
              /> : ''}
          isMainRetailer={false}
        />
        <TuileRow
          isBig={currentStore.projectedSales.average === extrema.salesMax
          && currentStore.projectedSales.average >= mainRetailer.sales}
          isGreen={
            currentStore.projectedSales.average >= mainRetailer.sales
          }
          label={currentStore.projectedSales.average
            ? (
              <CurrencySurfaceConvertor
                value={currentStore.projectedSales.average.toFixed(2)}
                withSymbol
                mustBeCurrencyBySurface
              />
            )
            : ''
          }
          isMainRetailer={false}
        />
        <TuileRow
          isBig={currentStore.projectedSbr === extrema.sbrMax
          && currentStore.projectedSbr >= mainRetailer.sbr}
          isGreen={currentStore.projectedSbr >= mainRetailer.sbr}
          label={currentStore.projectedSbr
            ? (
              <span>
                <CurrencySurfaceConvertor
                  value={currentStore.projectedSbr.toFixed(2)}
                  withSymbol
                  mustBeCurrencyBySurface
                />
                <small>
                  {currentStore.sbrPercent ? `(${currentStore.sbrPercent.toFixed(2)} %)` : ''}
                </small>
              </span>
            ) : ''
          }
          isMainRetailer={false}
        />
        <TuileRow
          label=""
          isMainRetailer={false}
        />
        <TuileRow
          isBig={currentStore.schRetailer === extrema.schRetailerMax &&
          currentStore.schRetailer >= mainRetailer.schRetailer}
          isGreen={currentStore.schRetailer >= mainRetailer.schRetailer}
          label={
            currentStore.schRetailer
              ? (
                <CurrencySurfaceConvertor
                  value={currentStore.schRetailer.toFixed(2)}
                  withSymbol
                  mustBeCurrencyBySurface
                />
              ) : ''}
          isMainRetailer={false}
        />
        <TuileRow
          isBig={currentStore.projectedOcr.average <= mainRetailer.ocr &&
          currentStore.projectedOcr.average === extrema.ocrMax
          }
          isGreen={currentStore.projectedOcr.average <= mainRetailer.ocr}
          label={currentStore.projectedOcr.average
            ? convertToPercentile(currentStore.projectedOcr.average, 2)
            : ''
          }
          isMainRetailer={false}
        />
        <TuileRow
          label={currentStore.foc
            ? (
              <CurrencySurfaceConvertor
                value={(currentStore.foc / currentStore.gla).toFixed(2)}
                withSymbol
                mustBeCurrencyBySurface
              />
            ) : ''}
          isGreen={currentStore.foc > mainRetailer.foc}
          isMainRetailer={false}
        />
        <TuileRow
          isGreen={currentStore.den && !!currentStore.den.toFixed(2)}
          label={currentStore.den
            ? (
              <CurrencySurfaceConvertor
                value={(currentStore.den / currentStore.gla).toFixed(2)}
                withSymbol
                mustBeCurrencyBySurface
              />
            ) : ''}
          isMainRetailer={false}
        />
        <TuileRow
          label={formatRoi(roi || 0, netMgrGlaUplift, tic)}
          isGreen={roi >= ROI_MIN}
          isMainRetailer={false}
        />
      </div>
      <div className="light-row larger bottom-button-container">
        <IconButton
          className="action-button-apollo apollo-button-contained"
          onClick={() => viewOffer(currentStore)}
          text={I18n.t('comparator.details')}
        />
        <IconButton
          disabled={isGenerationDisabled}
          isLoading={isGenerationLoading}
          onClick={() => !isGenerationDisabled && generateM30(currentStore)}
          className="action-button-apollo apollo-button-contained"
          text={I18n.t('comparator.rawData')}
          title={I18n.t('comparator.commercialOperation')}
        />
      </div>
    </div>
  );
}

export default TuileComparator;
